import * as React from "react";
import { WorkflowPropValue, WorkflowNode } from "../../types";
import { FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import { TertiaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib";
import { IconClose } from "@velaro/velaro-component-lib";
import VariableTextInput from "@/components/VariableTextInput";
import VariableHelpCard from "@/components/VariableHelpCard";
import FieldNamePicker from "@/components/FieldNamePicker";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SetDataEditor({ node, onUpdate }: Props) {
  const activeScopes = [
    node.saveAsContactProp && "contact",
    node.saveAsConversationProp && "conversation",
    node.saveAsWorkflowProp && "workflow"
  ].filter(Boolean) as string[];

  return (
    <>
      <VariableHelpCard context="setData" />
      <FormGroup label="Data to set">
        <div className="border rounded-lg p-2 mb-4 bg-slate-gray-100 flex flex-col items-center">
          {(node.dataToSet || []).map((data, i) => (
            <React.Fragment key={i}>
              <div className="flex w-full items-center gap-1 my-2">
                <div className="flex-1 min-w-0">
                  <FieldNamePicker
                    value={data.name}
                    onChange={(name) => {
                      const newData = [...node.dataToSet];
                      newData[i].name = name;
                      onUpdate({ dataToSet: newData });
                    }}
                    scopes={activeScopes.length > 0 ? activeScopes : undefined}
                    placeholder="Which variable to set?"
                  />
                </div>
                <IconClose
                  onClick={() => {
                    onUpdate({
                      dataToSet: node.dataToSet.filter(
                        (_, index) => index !== i
                      )
                    });
                  }}
                  className="cursor-pointer"
                  style={{ color: "#CBD5E1" }}
                />
              </div>
              <VariableTextInput
                placeholder="Value (type @ to insert a variable)"
                className="my-2"
                value={data.value}
                onChange={(value) => {
                  const newData = [...node.dataToSet];
                  newData[i].value = value;
                  onUpdate({ dataToSet: newData });
                }}
              />
              <div className="my-2 border border-slate-300 w-full"></div>
            </React.Fragment>
          ))}
          <TertiaryGrayButton
            label="Add Data"
            prefixIcon={<IconPlus />}
            className="w-full"
            onClick={() => {
              const newProp = {
                name: "",
                value: ""
              } as WorkflowPropValue;
              onUpdate({ dataToSet: [...(node.dataToSet || []), newProp] });
            }}
          />
        </div>
      </FormGroup>
      <FormGroup label="Save as">
        <ToggleGroup
          toggled={node.saveAsContactProp || false}
          label="Contact Variable"
          helpText="Permanent — saved to the contact record and available in every future conversation with this person. Use for: email, name, plan tier, preferences."
          changeToggle={() => {
            onUpdate({
              saveAsContactProp: !node.saveAsContactProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsConversationProp || false}
          label="Conversation Variable"
          helpText="This chat only — available throughout this conversation across all workflow steps, but cleared when the chat closes. Use for: ticket ID, issue type, order number for this session."
          changeToggle={() => {
            onUpdate({
              saveAsConversationProp: !node.saveAsConversationProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsWorkflowProp || false}
          label="Workflow Variable (advanced)"
          helpText="This step only — available within this single workflow run. Disappears after the workflow completes; nothing is saved. Use for: temporary counters, loop variables, intermediate calculations."
          changeToggle={() => {
            onUpdate({
              saveAsWorkflowProp: !node.saveAsWorkflowProp
            });
          }}
        />
      </FormGroup>
    </>
  );
}
